import React, { useMemo, useState } from "react";
// ANT DESIGN COMPONENTS
import { Empty } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// REDUX
import { useSelector } from "react-redux";
// CUSTOM HOOKS
import useBankActions from "../../../customHooks/GET_requests/useBankActions";
import useGetAllMessages from "../../../customHooks/GET_requests/useGetAllMessages";
// COMPONENTS
import ActionsPanel from "../Actions/ActionsPanel";
import ActionCard from "../Actions/components/ActionCard";
// INTERFACES
import { RootState } from "../../../app/store";
import { BankActionInterface, BankFloorInterface } from "../../../interfaces/Bank.interface";
import { actionIsBankLevel } from "../Actions/actionLiftScope";
// CONFIG
import { actionTypeOrDefault } from "../Actions/actionTypeConfig";
// UTILS
import { eligibleFloorOpenings, actionTakesFloor } from "../../../utils/controlFloorRules";
import { extractBankFloorOpenings } from "../../../utils/bankFloorOpenings";
import formatUuidT from "../../../flatbuffers/display/uuid";
// SCOPE
import { useScopePredicate } from "../../../customHooks/useScopedPermission";
import { PermissionAction } from "../../../flatbuffers/generated/archive/permission-action";

/**
 * The bank drawer's own "Actions" tab.
 *
 * The lift drawer's Actions tab (`ControlPanelLiftActions`) filters
 * `useBankActions` down to the rows scoped to the SELECTED car, since a
 * per-car command belongs to its own car only. This panel is the mirror
 * image: it keeps only the rows that carry NO lift scope at all — the
 * bank-level commands (`actionIsBankLevel`) — so a hall-wide or bank-wide
 * command shows here exactly once, never duplicated across every lift's
 * tab and never missing from both.
 *
 * There is no single car to gate a command's `validStates` against here, so
 * `carService` is passed as the same absent sentinel (-1) the lift tab uses
 * before a car has reported any service message — matching applyDeltaToState's
 * variantMessage, never 0 (Service.Auto), which would read as an already-Auto
 * car and disable buttons that should be enabled.
 *
 * Scheduled Actions is deliberately NOT rendered inside this panel: unlike
 * the lift drawer, the bank drawer already has its own top-level
 * "Scheduled Actions" tab (`ControlPanelBankScheduledActions`) gated on
 * `Permission.ScheduledActionsView`, so nesting it here would show it twice.
 */
export default function ControlPanelBankActions({
  bankId,
  bankName,
}: {
  bankId: string;
  bankName: string;
}) {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const [t] = useTranslation("global");

  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [disableTurnBack, setDisabledTurnBack] = useState<boolean>(false);

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { theme } = useSelector((state: RootState) => state.home);
  const { bankSelected } = useSelector((state: RootState) => state.monitoring);

  // ************************************************ */
  // CUSTOM HOOKS *********************************** */
  /**
   * This bank's configured actions — the SAME rows the lift Actions tab, the
   * bank Hall Calls / VIP panels and the lift Car Calls panel all read, so
   * this list can never disagree with them about what is configured.
   */
  const { actions } = useBankActions(bankId);
  const { messages } = useGetAllMessages();

  // ************************************************ */
  // FLOOR ELIGIBILITY ****************************** */
  // Per-floor ACTION scope predicate — empty grant rows ⇒ allow-all.
  const floorActionAllows = useScopePredicate(PermissionAction.ACTION);

  /**
   * An action's bound Control enum, or undefined when it resolves to none.
   *
   * "Unresolved" MUST be undefined, never 0. Control is an unnumbered,
   * append-only enum, so declaration order IS the wire value and the first
   * member — Control.CarCallLock — is a legitimate 0. Using 0 as the
   * absent-sentinel silently drops every CarCallLock action while 1-19 behave.
   */
  const controlEnumOf = useMemo(() => {
    const byId = new Map<string, number>();
    messages.forEach((m) => {
      const raw =
        (m as { enumId?: number; enum_id?: number }).enumId ?? (m as { enum_id?: number }).enum_id;
      if (typeof raw === "number") byId.set(String(m.id), raw);
    });
    return (action: BankActionInterface): number | undefined => byId.get(String(action.messageId));
  }, [messages]);

  /**
   * The bank's floors as opening records — the shape the shared eligibility rule
   * reads. `extractBankFloorOpenings` is what recovers a usable vertical order
   * when the `position` column is degenerate (identical, typically 0, across
   * every floor), which is the state real banks are in. Ranking on
   * `position ?? floorIndex` cannot: `??` does not fall back on a present 0, so
   * the top and the bottom read as the same floor and every up/down hall call
   * was excluded.
   */
  const bankFloorOpenings = useMemo(
    () => extractBankFloorOpenings({ bankFloors: bankSelected.bankFloors ?? [] }),
    [bankSelected.bankFloors],
  );

  /**
   * The floors one row may be sent to, or [] when the row takes no floor.
   *
   * Unlike the lift drawer's equivalent, there is no single car's served
   * floors to intersect against here — a bank-level command is offered every
   * eligible bank floor, restricted only by the operator's per-floor ACTION
   * scope grant.
   */
  const floorsForAction = useMemo(() => {
    const bankFloors = (bankSelected.bankFloors ?? []) as BankFloorInterface[];

    return (action: BankActionInterface): BankFloorInterface[] => {
      const enumValue = controlEnumOf(action);
      if (
        !actionTakesFloor(action.takesPosition, enumValue, action.floorScope) ||
        enumValue === undefined
      )
        return [];
      if (bankFloors.length === 0) return [];

      const eligibleIds = new Set(
        eligibleFloorOpenings(enumValue, bankFloorOpenings).map((opening) => opening.id),
      );

      return (
        bankFloors
          .filter((floor) => eligibleIds.has(formatUuidT(floor.id)))
          // Out-of-scope floors are HIDDEN rather than disabled. Master admin and
          // empty grant rows ⇒ allow-all.
          .filter((floor) =>
            floorActionAllows({
              bankId,
              bankFloorId: formatUuidT(floor.id),
            }),
          )
          // Top floor first, so the strip reads the way the building stands.
          .sort((a: BankFloorInterface, b: BankFloorInterface) => b.floorIndex - a.floorIndex)
      );
    };
  }, [bankSelected.bankFloors, bankFloorOpenings, controlEnumOf, floorActionAllows, bankId]);

  // ************************************************ */
  // ACTION LIST *********************************** */
  /**
   * ONE card per row `/action-banks/by-bank/:bankId` returned that carries NO
   * lift scope — the bank-level commands. Per-car rows belong on their own
   * lift's Actions tab and are excluded here so nothing is shown twice.
   */
  const cards = useMemo(
    () =>
      actions
        .filter((action: BankActionInterface) => action.deletedAt == null)
        .filter((action: BankActionInterface) => actionIsBankLevel(action))
        .slice()
        .sort((a: BankActionInterface, b: BankActionInterface) =>
          a.createdAt.localeCompare(b.createdAt),
        )
        .map((action: BankActionInterface) => ({
          action,
          type: actionTypeOrDefault(action.buttonType),
          floors: floorsForAction(action),
        })),
    [actions, floorsForAction],
  );

  // ************************************************ */
  // COMPONENT ************************************** */
  const hasCards = cards.length > 0;

  return (
    <div className="monitoringView__detailsBox" style={{ minHeight: "400px", height: "auto" }}>
      <div
        className={`monitoringView__controlPanel monitoringView__controlPanel__${theme}`}
        style={{
          minHeight: "400px",
          height: "auto",
          paddingBottom: "25px",
        }}
      >
        <ActionsPanel
          title={t("general.actions", "Actions")}
          subtitle={t(
            "monitoringActions.bankPanelSubtitle",
            "Execute configured commands for {{bankName}}.",
            { bankName: bankName || bankSelected.name || "" },
          )}
          helpText={t(
            "monitoringActions.bankPanelHelp",
            "Each row is one bank-level configured command. Per-car commands are on that car's own Actions tab.",
          )}
          theme={theme}
        >
          {hasCards ? (
            cards.map((card) => (
              <ActionCard
                key={card.action.id}
                action={card.action}
                typeName={card.type.typeName}
                config={card.type.config}
                // No single car is selected on the bank drawer, so there is no
                // service mode to gate against — matching the absent sentinel
                // ControlPanelLiftActions falls back to before a car has
                // reported any service message.
                carService={-1}
                theme={theme}
                disableTurnBack={disableTurnBack}
                setDisableTurnBack={setDisabledTurnBack}
                floors={card.floors}
              />
            ))
          ) : (
            <div className="actionsPanel__section generalStyles__flexCenter">
              <Empty description={<span>{t("general.noActionsAssigned")}</span>} />
            </div>
          )}
        </ActionsPanel>
      </div>
    </div>
  );
}
